/** @graphql cc */

import gql from 'graphql-tag';
import type { FC } from 'react';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { styled } from '@compiled/react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';
import type { ExecutionResult } from 'react-apollo';
import { defineMessages, FormattedMessage, useIntl, type MessageDescriptor } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { ChromelessEditor } from '@atlaskit/editor-core/appearance-editor-chromeless';

import type { MutationFunctionInteropSupportedOptions } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import {
	ADD_INLINE_COMMENT_EXPERIENCE,
	ADD_INLINE_COMMENT_LOAD_EXPERIENCE,
	ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
	VIEW_INLINE_COMMENT_EXPERIENCE,
	VIEW_PAGE_DIRECT_COMMENT_DATA_FLOW_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { selectInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience.ts';
import { createInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/createInlineCommentCompoundExperience.ts';
import { startEditorSubExperience } from '@confluence/experience-tracker/entry-points/startExperienceForRoute.ts';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/experience-tracker/entry-points/getCommentsDataManagerExperimentAttributes.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { clearCommentDraft } from '@confluence/comment/entry-points/draftUtils.ts';
import { CommentEditor } from '@confluence/comment/entry-points/LazyCommentEditor.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useCommentsContentActions } from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { usePageInfo } from '@confluence/page-info/entry-points/usePageInfo.ts';
import { InlineCommentFramework } from '@confluence/inline-comments-common/entry-points/InlineCommentFramework.ts';
import { CommentAuthor } from '@confluence/inline-comments-common/entry-points/CommentAuthor.ts';
import {
	parseError,
	getTranslatedError,
	isOutOfDateError,
	isHighlightError,
	isUnexpectedError,
	isAnnotationError,
} from '@confluence/inline-comments-common/entry-points/inlineCommentsUtils.ts';
import { useCreateInlineCommentMutation } from '@confluence/comments-data-manager/entry-points/useCreateInlineCommentMutation.ts';
import { type useCreateInlineCommentMutation$data } from '@confluence/comments-data-manager/entry-points/useCreateInlineCommentMutationTypes.ts';
import { useDocumentUpdateStatus } from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext.ts';
import { i18n as inlineCommentCommonI18n } from '@confluence/inline-comments-common/entry-points/i18n.ts';
import { constructStepForGql } from '@confluence/comments-util/entry-points/rendererAnnotations.ts';
import { usePageClass } from '@confluence/comments-hooks/entry-points/usePageClass.ts';
import { useShouldCommentSidebarBeSticky } from '@confluence/inline-comments-hooks/entry-points/useShouldCommentSidebarBeSticky.ts';
import { useCommentSidebarOffset } from '@confluence/inline-comments-hooks/entry-points/useCommentSidebarOffset.ts';
import { useInlineCommentsActions } from '@confluence/inline-comments-hooks/entry-points/useInlineComments.ts';
import { END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import {
	useCommentsDataActions,
	CommentType,
	type CommentData,
	type ReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type { TopLevelComment as BaseComment } from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import { fg } from '@confluence/feature-gating';
import { useWindowSize } from '@confluence/dom-helpers/entry-points/useWindowSize.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useContentRefetchDispatch } from '@confluence/content-body/entry-points/ContentRefetchContext.ts';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { DIRECT_COMMENT_DATA_FLOW_METRIC } from './perf.config.ts';
import type {
	CreateCommentInlineCommentMutation as CreateCommentInlineCommentMutation$data,
	CreateCommentInlineCommentMutationVariables as CreateCommentInlineCommentMutation$variables,
	CreateCommentInlineCommentMutation_createInlineComment_Comment_location_InlineComment as CreateInlineCommentLocation,
	ContentRepresentation,
} from './__types__/CreateCommentInlineCommentMutation.ts';
import { CommentSidebar } from './CommentSidebar.tsx';
import type { SelectionOptions } from './renderer/SelectionComponent.tsx';

export const CREATE_TIMEOUT = ExperienceTimeout.CREATE_COMMENT_PUBLISH;

const relativeFilePath = 'annotation-provider-inline-comments/CreateComment';

const i18n = defineMessages({
	unableToApplyTitle: {
		id: 'annotation-provider-inline-comments.create.error.unable.to.apply.title',
		defaultMessage: 'Something went wrong',
		description: 'The title of the flag shown to a user something went wrong',
	},
	unableToApplyDescription: {
		id: 'annotation-provider-inline-comments.create.error.unable.to.apply.description',
		defaultMessage: 'We failed to display your comment. To view it, refresh now.',
		description:
			'A message to inform the user we failed to show their newly created comment could not be shown (Comment is a noun)',
	},
	refreshContentLink: {
		id: 'annotation-provider-inline-comments.refresh.content.link',
		defaultMessage: 'Refresh content',
		description: 'A link for the user to click to refresh the content',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	padding: `${token('space.150')} ${token('space.200')}`,
});

type CreateCommentProps = {
	pageId: string;
	annotationElement?: HTMLElement | null;
	selectionOptions?: SelectionOptions;
	onCreate: (annotationId: string, commentData?: any) => void;
	onClose?: () => void;
	isEditor?: boolean;
	isOpeningMediaCommentFromToolbar?: boolean;
	isOffline?: boolean;
};

const getNodeType = (element: HTMLElement, annotationId: string) => {
	const nodeWithAnnotationWrapper = element.querySelector(`[id="${annotationId}"]`);
	if (nodeWithAnnotationWrapper) {
		const firsChildren = nodeWithAnnotationWrapper.firstChild;
		if (firsChildren?.nodeType === Node.TEXT_NODE) {
			return 'text';
		}

		const targetNode = nodeWithAnnotationWrapper.querySelector('[data-node-type]');
		if (targetNode) {
			return (targetNode as HTMLElement).dataset.nodeType;
		}
	}

	return 'unknown';
};

// Exporting for testing cases
export const handleDraftAnnotationDeletion =
	(setWasDraftAnnotationRemoved: (isRemoved: boolean) => void) =>
	(mutationList: MutationRecord[], observer: MutationObserver) => {
		for (const mutation of mutationList) {
			if (mutation.type === 'childList') {
				if (mutation.removedNodes.length > 0) {
					mutation.removedNodes.forEach((node) => {
						const nodeClassList = Array.from((node as HTMLElement).classList ?? []);

						// COMMENTS-4308 - If any part of the draft annotation was removed, check if the entirity of the draft
						// 				   was removed, if so, we need to disable the editor buttons as the comment will fail
						if (nodeClassList.includes('ak-editor-annotation-draft')) {
							if (document.querySelector('.ak-editor-annotation-draft') === null) {
								// Tell the editor that the draft annotation was removed
								setWasDraftAnnotationRemoved(true);

								createInlineCommentCompoundExperience.initExperience.debugPoint(
									`CreateComment dialog - draft annotation removed`,
								);

								// Disconnect the observer since we no longer need it
								observer.disconnect();
							}
						}
					});
				}
			}
		}
	};

const CreateCommentComponent: FC<CreateCommentProps & WithFlagsProps> = ({
	pageId,
	annotationElement,
	selectionOptions,
	onClose,
	onCreate,
	isEditor,
	isOpeningMediaCommentFromToolbar,
	flags,
	isOffline,
}) => {
	const started = useRef(false);
	const pageClass = usePageClass();
	if (started.current === false) {
		createInlineCommentCompoundExperience.draftToPublishExperience.start();

		started.current = true;
	}

	const initialisedSelection = useRef(false);
	if (
		selectionOptions &&
		selectionOptions?.createdFrom === 'EDITOR' &&
		initialisedSelection.current === false
	) {
		initialisedSelection.current = true;
		if (selectionOptions.inlineNodeTypes) {
			createInlineCommentCompoundExperience.addCommonAttributes({
				inlineNodeTypes: selectionOptions.inlineNodeTypes,
			});
		}
	}

	const { formatMessage } = useIntl();
	const pageMode = useGetPageMode();
	const lastContentFetchTime = useRef(1);
	const requestTimeoutHandle = useRef<ReturnType<typeof setTimeout>>();
	const sidebarEl = useRef<HTMLDivElement | null>(null);
	const draftAnnotationMutationObserverRef = useRef<MutationObserver>();
	const commentContainerElementRef = useRef<HTMLDivElement | null>(null);
	const { documentUpdated, publishedDocumentVersion } = useDocumentUpdateStatus();
	const { setRefetchContent } = useContentRefetchDispatch();

	const [shouldRefetch, setShouldRefetch] = useState(false);
	const [wasDraftAnnotationRemoved, setWasDraftAnnotationRemoved] = useState(false);

	const experienceTracker = useContext(ExperienceTrackerContext);
	const { onChange, resetContentChanged } = useCommentsContentActions();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { addUnresolvedInlineComment } = useInlineCommentsActions();
	const { userId, cloudId } = useSessionData();
	const { addNewCommentThreads } = useCommentsDataActions();
	const { width: windowWidth } = useWindowSize();
	const { isCommentsPanelShown } = useShowCommentsPanel();

	const { isSticky } = useShouldCommentSidebarBeSticky({
		annotationElement,
		isEditor: !!isEditor,
	});

	const sidebarOffset = useCommentSidebarOffset({
		isEditor: Boolean(isEditor),
		annotationElement,
		windowWidth,
		isSticky,
	});

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const [createCommentFn] = useMutation(gql`
		mutation CreateCommentInlineCommentMutation(
			$input: CreateInlineCommentInput!
			$pageId: ID!
			$includeReactionsAndReplies: Boolean!
		) {
			createInlineComment(input: $input) {
				id
				body(representation: ATLAS_DOC_FORMAT) {
					value
				}
				contentStatus
				createdAt {
					value
				}
				createdAtNonLocalized
				links {
					webui
					editui
				}
				location {
					type
					... on InlineComment {
						inlineMarkerRef
						inlineText
						inlineResolveProperties {
							resolved
							isDangling
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
					}
					... on FooterComment {
						commentResolveProperties {
							resolved
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
					}
				}
				parentId
				permissions {
					isEditable
					isRemovable
					isResolvable
				}
				version {
					by {
						displayName
					}
					when
					number
				}
				author {
					displayName
					profilePicture {
						path
					}
					permissionType
					... on KnownUser {
						accountId
					}
				}
				createdAt {
					value
				}
				createdAtNonLocalized
				location {
					type
					... on InlineComment {
						inlineMarkerRef
						inlineText
						inlineResolveProperties {
							isDangling
							resolvedTime
							resolvedFriendlyDate
							resolvedUser
							resolved
						}
					}
					... on FooterComment {
						commentResolveProperties {
							resolved
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
					}
				}
				reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment")
					@include(if: $includeReactionsAndReplies) {
					ari
					containerAri
					reactionsCount
					reactionsSummaryForEmoji {
						id
						emojiId
						count
						reacted
					}
				}
				replies @include(if: $includeReactionsAndReplies) {
					id
					body(representation: ATLAS_DOC_FORMAT) {
						value
					}
					contentStatus
					createdAt {
						value
					}
					createdAtNonLocalized
					links {
						webui
						editui
					}
					location {
						type
						... on InlineComment {
							inlineMarkerRef
							inlineText
							inlineResolveProperties {
								resolved
								isDangling
								resolvedFriendlyDate
								resolvedTime
								resolvedUser
							}
						}
						... on FooterComment {
							commentResolveProperties {
								resolved
								resolvedFriendlyDate
								resolvedTime
								resolvedUser
							}
						}
					}
					parentId
					permissions {
						isEditable
						isRemovable
						isResolvable
					}
					version {
						by {
							displayName
						}
						when
						number
					}
					author {
						displayName
						profilePicture {
							path
						}
						permissionType
						... on KnownUser {
							accountId
						}
					}
					createdAt {
						value
					}
					createdAtNonLocalized
					reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
						ari
						containerAri
						reactionsCount
						reactionsSummaryForEmoji {
							id
							emojiId
							count
							reacted
						}
					}
				}
			}
		}
	`);

	const { createInlineCommentInterop } = useCreateInlineCommentMutation({ contentId: pageId });

	const { pageInfo, loading, error, refetch } = usePageInfo({
		fetchPolicy: 'cache-first',
		onError: (err) => {
			experienceTracker.stopOnError({
				name: ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
				error: err,
			});
		},
	});

	useEffect(() => {
		// should only refresh on the Content Out of date `Refresh` CTA is clicked
		if (documentUpdated && shouldRefetch && refetch) {
			void refetch();
			setShouldRefetch(false);
			onClose && onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [documentUpdated, shouldRefetch]);

	useEffect(() => {
		const metadata = document.querySelector('meta[name=confluence-request-time]');

		if (metadata) {
			const fetchTime = Number(metadata.getAttribute('content'));
			lastContentFetchTime.current = isNaN(fetchTime) ? 1 : fetchTime;
		}

		if (pageMode !== PageMode.VIEW && fg('confluence_frontend_content_wrapper')) {
			const editorTextArea = document.getElementById('ak-editor-textarea');
			draftAnnotationMutationObserverRef.current = new MutationObserver(
				handleDraftAnnotationDeletion(setWasDraftAnnotationRemoved),
			);

			// We need to watch the whole editorTextArea because highlights could
			if (editorTextArea) {
				draftAnnotationMutationObserverRef.current.observe(editorTextArea, {
					childList: true,
					subtree: true,
				});
			}
		}

		return () => {
			if (
				pageMode !== PageMode.VIEW &&
				draftAnnotationMutationObserverRef.current &&
				fg('confluence_frontend_content_wrapper')
			) {
				draftAnnotationMutationObserverRef.current.disconnect();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Check for highlight element on mount - fail experience if createComment renders without a highlight
	// This tracks the scenario where createComment re-renders on close of the comment box without a highlight
	useEffect(() => {
		if (!expValEquals('confluence_comment_enable_exp_create_rerenderer', 'isEnabled', true)) {
			return;
		}
		// Use a small delay to allow the DOM to settle after any state changes
		const timeoutId = setTimeout(() => {
			const draftAnnotationElement = __getDraftAnnotationElement();

			// Fail experience if createComment renders without a highlight
			if (!draftAnnotationElement && !wasDraftAnnotationRemoved) {
				createInlineCommentCompoundExperience.initExperience.fail(
					new Error('CreateComment renders without highlight'),
				);
			}
		}, 100);

		return () => clearTimeout(timeoutId);
		// Only run on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const saveAttemptedTracker = useRef<{
		totalSaves: number;
		failedSaves: number;
		lastPublishFailure?: Error;
		lastMutationSucceeded: boolean;
	}>({
		lastMutationSucceeded: false,
		totalSaves: 0,
		failedSaves: 0,
	});
	useEffect(() => {
		return () => {
			const savesWithoutError =
				// hit, the experience will already have completed in the `handleSaveComment`.
				saveAttemptedTracker.current.totalSaves - saveAttemptedTracker.current.failedSaves;

			if (saveAttemptedTracker.current.failedSaves > 0) {
				createInlineCommentCompoundExperience.addCommonAttributes({
					// eslint-disable-next-line react-hooks/exhaustive-deps
					failedSaves: saveAttemptedTracker.current.failedSaves,
				});
			}

			if (savesWithoutError > 1) {
				// At the point this is dismounting -- we don't know for certain if the user intended for this to be discarded or not
				// so we hard fail the full experience (it's possible the dismount is because the second save has worked and
				// the UI is about to load the new comment)
				createInlineCommentCompoundExperience.addCommonAttributes({
					savesWithoutError,
				});
				const msg = 'User clicked save, but UI did not update following successful save';
				// we log a debug point and continue with the flow, since creation is not necessarily failing
				createInlineCommentCompoundExperience.draftToPublishExperience.debugPoint(msg);
			}

			if (saveAttemptedTracker.current.lastPublishFailure) {
				if (saveAttemptedTracker.current.lastMutationSucceeded) {
					// in the case there was a publish failure, but the last mutation succeeded -- we assume the dismount is
					// because the ui is about to load the new comment - in this case we only mark the experience as a soft
					// failure (we'll capture more details and fail in the final experience step)
					createInlineCommentCompoundExperience.draftToPublishExperience.softFail(
						// eslint-disable-next-line react-hooks/exhaustive-deps -- This is intentionally using a ref value due to the indirect flow of comment updates
						saveAttemptedTracker.current.lastPublishFailure,
					);

					return;
				} else {
					// In this scenario -- the user attempted to save at least once, got a network error, and gave up on
					// trying to create the comment

					// For a subset of errors -- the user is presented with a human readable error message describing that
					// the comment creation failure is due to the content changing (e.g. the user has edited the page) and
					// that they need to refresh the page to apply the comment.  In this scenario we don't treat the comment
					// creation as a failure, but rather we want to mark the experience as aborted.
					const contentChangedErrorSubstrings = [
						'Invalid arguments provided for matchIndex and/or numMatches',
						'Failed to insert highlight, invalid originalSelection, numMatches, or matchIndex',
						'Selection was made against a stale version of the content',
						'Published Version out of date. Please provide correct published version.',
					];
					const isContentChangedError = contentChangedErrorSubstrings.some((substring) =>
						// eslint-disable-next-line react-hooks/exhaustive-deps
						saveAttemptedTracker.current.lastPublishFailure?.message.includes(substring),
					);
					if (isContentChangedError) {
						createInlineCommentCompoundExperience.draftToPublishExperience.abort({
							abortReason: 'Unable to apply annotation to document',
						});

						return;
					}

					// This is handled in the UI appropriately, and we should abort not fail
					const permissionErrorSubstring = 'Could not create content with type comment';
					const isPermissionError =
						saveAttemptedTracker.current.lastPublishFailure?.message.includes(
							permissionErrorSubstring,
						);

					if (isPermissionError) {
						createInlineCommentCompoundExperience.draftToPublishExperience.abort({
							abortReason: 'User does not have space permission to create comments',
						});
					}

					createInlineCommentCompoundExperience.draftToPublishExperience.stopOnError(
						// eslint-disable-next-line react-hooks/exhaustive-deps -- This is intentionally using a ref value due to the indirect flow of comment updates
						saveAttemptedTracker.current.lastPublishFailure,
					);

					return;
				}
			}

			if (saveAttemptedTracker.current.lastMutationSucceeded) {
				createInlineCommentCompoundExperience.draftToPublishExperience.complete();

				return;
			}

			// In this case the user did not attempt to save the comment, and so we assume this was
			// an intentional discard of the comment.
			// So we abort the draftToPublishExperience here, along with any unfinished initExperience.
			const abortReason = 'Create Comment UI dismissed';
			createInlineCommentCompoundExperience.draftToPublishExperience.abort({ abortReason });
			createInlineCommentCompoundExperience.initExperience.abort({ abortReason });

			DIRECT_COMMENT_DATA_FLOW_METRIC.cancel();
		};
	}, []);

	const saveAttemptedCounts = useRef<{ total: number; failedSaves: number }>({
		total: 0,
		failedSaves: 0,
	});

	const failPublishExperience = useCallback(
		(commentPublishError: Error, translatedErrorMsg: MessageDescriptor) => {
			// COMMENTS-1879 - Only log unexpected errors or annotation application error as failures
			if (isUnexpectedError(translatedErrorMsg)) {
				experienceTracker.stopOnError({
					name: ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
					error: commentPublishError,
				});
			} else {
				markErrorAsHandled(commentPublishError);
			}
		},
		[experienceTracker],
	);

	const onCacheUpdateError = useCallback(
		(cacheError: Error) => {
			const { message } = parseError(cacheError);
			failPublishExperience(cacheError, getTranslatedError(message));
		},
		[failPublishExperience],
	);

	const handleSaveCommentWithTimeout = (timeoutInMs: number, saveMutation: Promise<void>) => {
		const timeout = new Promise((_resolve, reject) => {
			requestTimeoutHandle.current = setTimeout(() => {
				experienceTracker.stopOnError({
					name: ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
					error: new Error(`Request took longer than ${timeoutInMs}ms`),
				});

				return reject({ error: inlineCommentCommonI18n.createTimeout });
			}, timeoutInMs);
		});

		return Promise.race([timeout, saveMutation]).finally(() => {
			if (requestTimeoutHandle.current) {
				clearTimeout(requestTimeoutHandle.current);
			}
		});
	};

	const handleSaveComment = (adf: JSONDocNode, onSuccess: () => void) => {
		saveAttemptedTracker.current.totalSaves = saveAttemptedTracker.current.totalSaves + 1;
		saveAttemptedTracker.current.lastMutationSucceeded = false;
		saveAttemptedCounts.current.total = saveAttemptedCounts.current.total + 1;

		if (!selectionOptions) {
			// We likely will never get here, but just in case
			return Promise.reject({ error: inlineCommentCommonI18n.unableToGenerateStep });
		}

		startEditorSubExperience({
			name: VIEW_PAGE_DIRECT_COMMENT_DATA_FLOW_EXPERIENCE,
			attributes: {
				pageId,
				pageMode,
				isEditor,
			},
		});
		DIRECT_COMMENT_DATA_FLOW_METRIC.start();
		DIRECT_COMMENT_DATA_FLOW_METRIC.mark('commentCreationStart');
		startEditorSubExperience({
			name: ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
			attributes: {
				timeout: CREATE_TIMEOUT,
			},
		});

		const { numMatches, matchIndex, originalSelection, createdFrom, step, targetNodeType } =
			selectionOptions;
		// eslint-disable-next-line confluence-feature-gating/inline-usage
		const variables = {
			input: {
				containerId: pageId,
				commentBody: {
					value: JSON.stringify(adf),
					representationFormat: 'ATLAS_DOC_FORMAT' as ContentRepresentation,
				},
				numMatches,
				matchIndex,
				originalSelection,
				createdFrom,
				lastFetchTimeMillis: lastContentFetchTime.current,
				publishedVersion: publishedDocumentVersion,
				...(!!step && { step: constructStepForGql(step) }),
			},
			pageId,
			includeReactionsAndReplies: false,
		};

		// Note -- this mixes data saving logic and UI application logic.
		// And when the UI logic fails, the result is that the user is left with
		// the ui looking like the comment failed to save.
		// This should be refactored to separate the two concerns, and on a UI application
		// failure, we should encourage users to refresh (and mark the experience as failed).
		let succeededPublish = false;

		const onCreateSuccess = (
			retVal: ExecutionResult<
				CreateCommentInlineCommentMutation$data | useCreateInlineCommentMutation$data
			>,
		) => {
			saveAttemptedTracker.current.lastMutationSucceeded = true;
			const { data } = retVal;
			if (requestTimeoutHandle.current) {
				clearTimeout(requestTimeoutHandle.current);
			}

			succeededPublish = true;
			createInlineCommentCompoundExperience.draftToPublishExperience.publishSucceeded();

			// Mark comment creation end for both cohorts
			DIRECT_COMMENT_DATA_FLOW_METRIC.mark('commentCreationEnd');
			DIRECT_COMMENT_DATA_FLOW_METRIC.mark('commentDataLoadStart');

			const annotationId = (data?.createInlineComment?.location as CreateInlineCommentLocation)
				?.inlineMarkerRef;
			const commentId = data?.createInlineComment?.id;

			if (!annotationId || !commentId) {
				throw new Error(
					`Comment was not saved correctly - either annotationId '${annotationId}' or commentId '${commentId}' were undefined`,
				);
			}

			// Reset the content changing flag
			resetContentChanged();

			// Grab the marker ref id returned and update the editor/renderer
			try {
				onCreate(annotationId, data?.createInlineComment);
			} catch (e) {
				// We generate errors in renderer, but this will also handle the editor case
				throw new Error(`Unable to apply annotation to document - ${e.message}`);
			}

			// Update the unresolved inline comments count
			addUnresolvedInlineComment(annotationId, pageMode);

			// For experiment cohort, we pass the mutation data to onCreate
			// The experiment success and data load completion will be handled in AnnotationsContext
			// after the view component renders the new data

			const newComment: CommentData = {
				// (Need to coerce the data from the new Comments Data Manager's mutation because it is
				// defined using the new Relay-generated types that define option fields as "| null | undefined"
				// but the original Apollo-generated types define them as just "| null".  These aren't compatible
				// without the coercion even though the "if (value)" or "value?." expressions would work the same.)
				...(data?.createInlineComment as BaseComment),
				isOpen: true,
				type: CommentType.INLINE,
				wasRemovedByAnotherUser: false,
				replies: [] as ReplyData[],
				reactionsSummary: null,
			};

			addNewCommentThreads({
				general: {},
				inline: {
					[annotationId]: newComment,
				},
			});

			// Create inline comment track event
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'created',
					actionSubject: 'comment',
					actionSubjectId: commentId,
					objectType: 'page',
					objectId: pageId,
					source: isEditor ? 'editPageScreen' : 'viewPageScreen',
					attributes: {
						...getCommentsDataManagerExperimentAttributes(),
						source: AnalyticsSource.STANDALONE,
						commentType: CommentType.INLINE,
						parentCommentId: null,
						isReply: false,
						pageMode,
						isLivePage: createdFrom === 'LIVE',
						navdexPointType: END,
						inlineNodeTypes: selectionOptions.inlineNodeTypes,
						...(annotationElement &&
							!isEditor && {
								nodeType: getNodeType(annotationElement, annotationId),
							}),
						...(!isEditor && { targetNodeType }),
					},
				},
			}).fire();

			// Reset the editor
			onSuccess();

			selectInlineCommentCompoundExperience.selectAnnotation.start({
				pageClass,
				commentType: 'inline',
				entryPoint: 'create-comment',
				annotationId,
			});

			/*
			 * On editor pages, the view-inline-comment experience is started by the editor when the
			 * created annotation is automatically selected after successful comment creation. For
			 * editor pages, we update the attributes here to ensure that the experience is tracked
			 * with the correct entry point.
			 */
			if (fg('confluence_frontend_view_inline_comment_create')) {
				const attributes = {
					annotationId,
					entryPoint: relativeFilePath,
					pageClass,
				};

				if (pageClass === 'renderer') {
					experienceTracker.start({
						name: VIEW_INLINE_COMMENT_EXPERIENCE,
						timeout: ExperienceTimeout.VIEW_INLINE_COMMENT,
						attributes: {
							...getCommentsDataManagerExperimentAttributes(),
							...attributes,
						},
					});
				} else if (pageClass === 'editor') {
					experienceTracker.updateAttributes({
						name: VIEW_INLINE_COMMENT_EXPERIENCE,
						attributes,
					});
				}

				if (isCommentsPanelShown) {
					experienceTracker.fail({
						name: VIEW_INLINE_COMMENT_EXPERIENCE,
						error: new Error('Comments panel is visible when it should not be'),
						attributes: {
							stopPoint: relativeFilePath,
						},
					});
				}
			}
		};

		const onCreateFailure = (err: Error) => {
			saveAttemptedTracker.current.failedSaves = saveAttemptedTracker.current.failedSaves + 1;
			saveAttemptedCounts.current.failedSaves = saveAttemptedCounts.current.failedSaves + 1;

			experienceTracker.fail({
				name: VIEW_PAGE_DIRECT_COMMENT_DATA_FLOW_EXPERIENCE,
				error: err,
				attributes: {
					pageId,
					pageMode,
					isEditor,
					pointOfFailure: !succeededPublish ? 'running-mutation' : 'applying-comment-to-ui',
				},
			});
			DIRECT_COMMENT_DATA_FLOW_METRIC.cancel();

			createInlineCommentCompoundExperience.draftToPublishExperience.debug({
				error: err,
				createAnalyticsEvent,
				extraAttributes: {
					pointOfFailure: !succeededPublish ? 'running-mutation' : 'applying-comment-to-ui',
				},
			});

			if (!succeededPublish) {
				saveAttemptedTracker.current.lastPublishFailure = err;
			}
			let contentOutOfDate = false;
			// Get a truncated graphql error message
			const { errorId, message } = parseError(err);
			const translatedError = getTranslatedError(message);

			// COMMENTS-1879 - The backend will fall back to the old method of trying to make string matches if the content
			// is out of date. If the BE fails to apply a comment when using the old fallback method we should log it
			if (isHighlightError(translatedError)) {
				// Send the analytics event
				const analyticsObject = {
					source: 'viewPage',
					objectType: 'page',
					objectId: pageId,
					action: 'invalid',
					actionSubject: 'highlight',
					actionSubjectId: 'inlineComment',
					attributes: {
						errorMessage: message,
						editor: 'FABRIC',
						framework: InlineCommentFramework.ANNOTATION_PROVIDER,
						errorId,
						highlightOrigin: 'userHighlight', // TODO: Do we need to programmatically track this?
					},
				};

				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: analyticsObject,
				}).fire();

				contentOutOfDate = true;
			}

			failPublishExperience(err, translatedError);

			// Use the existing value if it's already set or is an out of date error
			contentOutOfDate = contentOutOfDate || isOutOfDateError(translatedError);

			// In the case of an annotation error, we should show a flag to the user to let them know
			// their comment has been saved and they need to refresh the content (view mode only)
			if (isAnnotationError(translatedError)) {
				const CREATE_ERROR_FLAG_ID = `create-error-${Date.now()}`;
				void flags.showWarningFlag({
					id: CREATE_ERROR_FLAG_ID,
					title: <FormattedMessage {...i18n.unableToApplyTitle} />,
					description: (
						<FormattedMessage
							{...(translatedError === inlineCommentCommonI18n.unableToApplyAnnotation
								? i18n.unableToApplyDescription
								: translatedError)}
						/>
					),
					actions:
						// Only show the refetch content action on view mode as it's not applicable in editor/live
						pageMode === PageMode.VIEW
							? [
									{
										content: formatMessage(i18n.refreshContentLink),
										onClick: () => {
											setRefetchContent(true);
											handleOnClose();
											void flags.hideFlag(CREATE_ERROR_FLAG_ID);
										},
									},
								]
							: // TODO: Add actions for editor/live if needed
								[],
				});

				// Log the occurance through analytics so we can still track it
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: pageMode === PageMode.VIEW ? 'viewPageScreen' : 'editPageScreen',
						objectType: 'page',
						objectId: pageId,
						action: 'failed',
						actionSubject: 'applyAnnotation',
						actionSubjectId: 'inlineComment',
						attributes: {
							errorMessage: message,
							editor: 'FABRIC',
							framework: InlineCommentFramework.ANNOTATION_PROVIDER,
							isLivePage: pageMode === PageMode.LIVE,
							tenantId: cloudId,
							userId,
							pageId,
						},
					},
				}).fire();

				createInlineCommentCompoundExperience.draftToPublishExperience.fail(
					new Error('Unable to apply annotation to document'),
					{
						errorMessage: message,
						...(errorId ? { errorId } : {}),
						pageMode,
						context: isEditor ? 'editor' : 'renderer',
						pointOfFailure: !succeededPublish ? 'running-mutation' : 'applying-comment-to-ui',
						pageId,
					},
				);

				onClose && onClose();

				return;
			}

			// CommentEditor expects a translated error message otherwise it fails
			// TODO: CommentEditor should likely just receive the translated message
			// rather than expect the object needed for FormattedMessage
			return Promise.reject({
				error: contentOutOfDate ? inlineCommentCommonI18n.contentOutOfDate : translatedError,
				contentOutOfDate,
			});
		};

		// Wrap the createCommentFn mutation with the interop logic that handles both the direct Apollo
		// mutation path and the new hook-based for the Comments Data Manager
		const commentCreationInteropWrapper = (
			options: MutationFunctionInteropSupportedOptions<
				CreateCommentInlineCommentMutation$data,
				CreateCommentInlineCommentMutation$variables
			>,
		) =>
			createInlineCommentInterop<
				CreateCommentInlineCommentMutation$data,
				CreateCommentInlineCommentMutation$variables
			>({
				originalMutationFn: createCommentFn,
				options,
				onCacheUpdateError,
			});

		if (fg('confluence_frontend_add_inline_comment_timeout')) {
			return handleSaveCommentWithTimeout(
				CREATE_TIMEOUT,
				commentCreationInteropWrapper({ variables }).then(onCreateSuccess).catch(onCreateFailure),
			);
		} else {
			return commentCreationInteropWrapper({ variables })
				.then(onCreateSuccess)
				.catch(onCreateFailure);
		}
	};

	const handleOnClose = useCallback(
		(hasContentChanged?: boolean) => {
			const reason = hasContentChanged
				? 'comment discarded by user'
				: 'empty comment discarded by user';

			experienceTracker.abort({
				name: ADD_INLINE_COMMENT_EXPERIENCE,
				reason,
			});

			// Clearing reply draft is handled in the comment editor
			clearCommentDraft(isEditor ? 'edit-inline' : 'inline', 'create');

			onClose && onClose();
		},
		[experienceTracker, isEditor, onClose],
	);

	const onEditorLoad = () => {
		experienceTracker.succeed({
			name: ADD_INLINE_COMMENT_LOAD_EXPERIENCE,
		});
		const draftAnnotationElement = __getDraftAnnotationElement();

		if (!draftAnnotationElement && !wasDraftAnnotationRemoved) {
			// We have handling to show a message when we detect the draft annotation was removed, indicated by
			// `wasDraftAnnotationRemoved=true`. So we only need to fail the experience if we unexpectedly cannot find
			// the draftAnnotationElement, indicated by `wasDraftAnnotationRemoved=false`
			createInlineCommentCompoundExperience.initExperience.fail(new Error('No annotation element'));

			return;
		}

		setTimeout(() => {
			// There are multiple useEffects involved in positioning the comment UI.
			// This works around this by only marking the experience as complete after
			// a short timeout + animation frame.
			requestAnimationFrame(() => {
				// In the Editor and Renderer, the comment UI is positioned relative to slightly different
				// elements.

				if (!draftAnnotationElement) {
					createInlineCommentCompoundExperience.initExperience.abort({
						abortReason: 'Draft annotation removed from document',
					});

					return;
				} else if (!sidebarEl.current) {
					createInlineCommentCompoundExperience.initExperience.fail(
						new Error('Missing create comment ui'),
					);
				} else {
					const commentUIVerticalDistanceFromAnnotation = Math.abs(
						draftAnnotationElement.getBoundingClientRect().top -
							sidebarEl.current.getBoundingClientRect?.().top,
					);

					// The 40 pixel allowance has been arbitrarily chosen to allow for some wiggle room
					if (commentUIVerticalDistanceFromAnnotation > 40) {
						const isVisible = sidebarEl.current.checkVisibility();
						if (isVisible) {
							// if it's fully visible, but misaligned, we don't treat this as a failure
							createInlineCommentCompoundExperience.initExperience.debug({
								error: new Error('View Created Comment UI too far from annotation'),
								createAnalyticsEvent,
								extraAttributes: { misalignedBy: commentUIVerticalDistanceFromAnnotation },
							});
							createInlineCommentCompoundExperience.initExperience.complete();
						} else {
							createInlineCommentCompoundExperience.addCommonAttributes({
								misalignedBy: commentUIVerticalDistanceFromAnnotation,
							});
							createInlineCommentCompoundExperience.initExperience.softFail(
								new Error('Draft Comment UI is setup too far from annotation'),
							);
						}
					} else {
						createInlineCommentCompoundExperience.initExperience.complete();
					}
				}
			});
		}, 1000);
	};

	if (loading || error) {
		createInlineCommentCompoundExperience.initExperience.debugPoint(
			`CreateComment dialog ${loading ? 'loading' : 'error'}`,
			{
				error: error?.message ?? '',
				loading,
			},
		);
		return null;
	}

	const spaceId = pageInfo?.space?.id ?? '';
	const pageType = pageInfo?.type ?? '';

	// User page permissions
	const operations = pageInfo?.operations || [];

	const canAddComment = operations.some(
		(op) => op?.operation === 'create' && op?.targetType === 'comment',
	);

	const canAddAttachment = operations.some(
		(op) => op?.operation === 'create' && op?.targetType === 'attachment',
	);

	// The user can upload media only if they have update permissions for the page if confluence_comment_use_comment_media_endpoint FG is off
	const hasMediaUploadPermissions = fg('confluence_comment_use_comment_media_endpoint')
		? canAddComment && canAddAttachment
		: operations.some((op) => op?.operation === 'update' && op?.targetType === pageType);

	return (
		<CommentSidebar
			pageId={pageId}
			onClose={handleOnClose}
			isEditor={isEditor}
			annotationElement={annotationElement}
			sidebarOffset={sidebarOffset}
			isOpeningMediaCommentFromToolbar={isOpeningMediaCommentFromToolbar}
			sidebarEl={sidebarEl}
			isSticky={isSticky}
			isFabricPage
		>
			<Container ref={commentContainerElementRef}>
				<CommentAuthor
					size={expValEquals('confluence_comments_tuneup', 'isEnabled', true) ? 'small' : 'medium'}
					commentMode="create"
					userId={userId}
				/>
				<CommentEditor
					// @ts-ignore FIXME: `pageId` can be `undefined` here, and needs proper handling
					pageId={pageId}
					pageType={pageType}
					spaceId={spaceId}
					appearance="chromeless"
					EditorComponent={ChromelessEditor}
					onSaveComment={handleSaveComment}
					onContentChange={onChange}
					commentMode="create"
					commentType={isEditor ? 'edit-inline' : 'inline'}
					onEditorReady={onEditorLoad}
					hideWatchCheckbox
					pageMode={isEditor ? 'edit' : 'view'}
					shouldWarnOnInternalNavigation
					useNewWarningModal
					setShouldRefetch={() => setShouldRefetch(true)}
					hasMediaUploadPermissions={hasMediaUploadPermissions}
					commentContainerElementRef={commentContainerElementRef}
					wasDraftAnnotationRemoved={wasDraftAnnotationRemoved}
					isStandaloneComment
					isOffline={isOffline}
				/>
			</Container>
		</CommentSidebar>
	);
};

export const CreateComment = withFlags(CreateCommentComponent);

/**
 *
 * Exported for testing purposes only
 *
 * This function is used to find the draft annotation element in the DOM.
 * It checks both the renderer and editor for the draft annotation element.
 *
 * In some scenarios, there are duplicate draft annotations in the DOM such
 * as in the renderer where the dom for table headers is repeated.
 *
 * To mitigate this, we get all possible draft annotation elements and
 * and return the first one that has a height greater than 0.
 *
 * If no draft annotation element is found, it returns null.
 */
export function __getDraftAnnotationElement(): HTMLElement | null {
	const possibleAnnotations = document.querySelectorAll(
		'[data-annotation-draft-mark="true"], .ak-editor-annotation-draft',
	);

	if (possibleAnnotations.length === 1) {
		// In cases where there is only one draft annotation, we can return it directly
		// and avoid the performance hit where reflow is triggered by checking if the
		// annotation has a height greater than 0.
		return possibleAnnotations[0] as HTMLElement;
	}

	for (let i = 0; i < possibleAnnotations.length; i++) {
		if (possibleAnnotations[i].getBoundingClientRect().height > 0) {
			return possibleAnnotations[i] as HTMLElement;
		}
	}

	return null;
}
